import { DeleteOutline, Recycling } from '@mui/icons-material';
import { alpha, Box, IconButton, Typography } from '@mui/material';
import { PointsIcon } from './PointsIcon';
import { Deposit, Item, ItemType, Material } from 'types/api.types';
import React from 'react';

interface DepositCardProps {
  item: Deposit;
  items: Item[];
  materials: Material[];
  hideDelete?: boolean;
  onDeleteClick(): void;
}

const DepositCard: React.FC<DepositCardProps> = ({
  item,
  items,
  materials,
  hideDelete,
  onDeleteClick,
}) => {
  const depositItem = items.find(({ id }) => id === item.item);
  const displayName =
    item.product?.trim() || item.itemName || depositItem?.name;
  return (
    <Box
      sx={(theme) => ({
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        boxShadow: '0px 2px 16px rgba(8, 8, 8, 0.15)',
        borderRadius: '8px',
        background: theme.palette.common.white,
        mb: 1,
        p: 0,
      })}
    >
      <Box
        component="img"
        src={item.imageUrl}
        alt={`${item.material} - ${item.item}`}
        sx={{
          w: 3,
          h: 3,
          maxWidth: 100,
          maxHeight: 100,
          minWidth: 100,
          minHeight: 100,
          borderRadius: 1,
          objectFit: 'cover',
          objectPosition: 'center',
          display: 'block',
        }}
      />
      <Box sx={{ display: 'flex', justifyContent: 'space-between', flex: 1, px: 2 }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-start',
          }}
        >
          <Typography
            variant="body1"
            sx={{
              flexGrow: 1,
              fontWeight: 600,
              lineHeight: 1.6,
            }}
          >
            {displayName}
          </Typography>
          <Typography variant="body2" sx={{ flexGrow: 1, mb: 1, opacity: 0.6, fontSize: 12 }}>
            {depositItem?.type === ItemType.Recycle &&
              materials.find(({ id }) => id === item.material)?.name}
            {depositItem?.type === ItemType.Reuse && 'Reusable item'}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Recycling sx={(theme) => ({ fontSize: 24, color: theme.palette.primary.main })} />
            {depositItem?.type === ItemType.Recycle && (
              <Box
                sx={(theme) => ({
                  background:
                    (item.binCategories && item.binCategories[0]?.color) ||
                    theme.palette.primary.main,
                  color:
                    (item.binCategories && item.binCategories[0]?.contrastColor) ||
                    theme.palette.common.white,
                  px: 1,
                  py: 0.2,
                  borderRadius: 0.5,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                })}
              >
                <Typography variant="caption">
                  {(item.binCategories && item.binCategories[0]?.title) || 'Landfill'}
                </Typography>
              </Box>
            )}
            {depositItem?.type === ItemType.Reuse && (
              <Box
                sx={(theme) => ({
                  background: alpha(theme.palette.primary.main, 0.1),
                  color: theme.palette.primary.main,
                  px: 1,
                  py: 0.2,
                  borderRadius: 0.5,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                })}
              >
                <Typography color="inherit" variant="caption" sx={{ fontWeight: 600 }}>
                  Reuse
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-end',
            justifyContent: 'space-between',
          }}
        >
          {!hideDelete && (
            <IconButton onClick={onDeleteClick}>
              <DeleteOutline />
            </IconButton>
          )}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PointsIcon size={20} />
            <Typography sx={{ fontWeight: 600 }}>{item.points}</Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default DepositCard;
